import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { uniqBy } from "lodash";
import React, { useState } from "react";
import ClickAwayListener from "react-click-away-listener";
import { craneActions } from "../../data/actions";
import { CraneActionKey } from "../../data/keys";
import { alertDefinitions } from "../../data/alerts";
import { procedures } from "../../data/procedures";
import { useStore } from "../../store";
import { getActionAvailability } from "../../utils/alertHelpers";
import { IconButton } from "./IconButton";

type ImpossibleActionProps = {
  actionKey: CraneActionKey;
  open: boolean;
} & React.ComponentPropsWithoutRef<"li">;

const ImpossibleAction = ({
  actionKey,
  open,
  ...props
}: ImpossibleActionProps) => {
  const { alerts } = useStore();
  const action = craneActions[actionKey];
  const relevantAlerts = uniqBy(
    alerts.filter((alert) =>
      action.relevantAlertDefinitions.includes(alert.definitionId)
    ),
    "definitionId"
  );
  return (
    <li
      key={actionKey}
      className="flex items-center justify-between py-2 border-b border-crane-darker last:border-b-0 relative cursor-pointer"
      {...props}
    >
      <span className="text-crane-gray">{craneActions[actionKey].name}</span>
      <ChevronRightIcon className="w-4 h-4" />
      {open && (
        <div className="absolute top-0 right-0 translate-x-full w-96 pl-4">
          <ClickAwayListener
            onClickAway={(e) => props.onClick && props.onClick(e as any)}
          >
            <div className="bg-crane-dark border border-crane-darker rounded-lg shadow-xl p-4 w-full">
              <ul className="space-y-6">
                {relevantAlerts.map((alert) => {
                  const def =
                    alertDefinitions[
                      alert.definitionId as keyof typeof alertDefinitions
                    ];
                  return (
                    <li
                      key={`${alert.id}-${actionKey}-open`}
                      className="flex items-start"
                    >
                      <span className="mr-3">⚠️</span>
                      <div>
                        <div className="crane-text-body-lg font-medium text-crane-white">
                          {def?.title || alert.definitionId}
                        </div>
                        <div className="crane-text-body-lg text-crane-light-gray mt-1">
                          {def?.message || ""}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </ClickAwayListener>
        </div>
      )}
    </li>
  );
};

export const ProcedureInfo: React.FC = () => {
  const { currentProcedure, openModal, alerts, highlightedElement } =
    useStore();
  const [open, setOpen] = useState(false);
  const [openImpossibleAction, setOpenImpossibleAction] =
    useState<CraneActionKey | null>(null);

  const procedure = procedures[currentProcedure];

  const availability = getActionAvailability(alerts, procedure);
  const actionsBlocked = availability.notPossible.length;

  const handleClose = () => {
    setOpen(false);
    setOpenImpossibleAction(null);
  };

  const toggleOpen = () => {
    setOpen((v) => !v);
    setOpenImpossibleAction(null);
  };

  const handleImpossibleActionClick = (key: CraneActionKey) => {
    setOpenImpossibleAction((v) => (v === key ? null : key));
  };

  return (
    <div
      className={`flex flex-col items-center w-72 h-20 relative self-stretch `}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <div
          className={`absolute top-0 left-0 w-full   flex flex-col items-start justify-start z-10 ${
            open ? "bg-crane-dark border border-crane-darker rounded-lg" : ""
          }`}
        >
          <div className="p-4 h-20 flex flex-col justify-center">
            <div className="flex items-center space-x-2">
              <span className="crane-text-heading-lg font-medium">
                {procedure.name}
              </span>
              {/* <Cog6ToothIcon className="w-5 h-5 text-crane-gray hover:text-crane-white" /> */}
              <IconButton
                src="/assets/icon-cog.png"
                alt="settings"
                onClick={() => openModal("wizard")}
                imgClassName="w-6 h-6"
              />
            </div>

            <button
              type="button"
              onClick={toggleOpen}
              className={`flex items-center space-x-2 text-body-lg text-crane-gray hover:text-crane-white text-left ${
                highlightedElement === "movement-assistant"
                  ? "border-crane-accent border-2 rounded-md px-2 py-1 animate-pulse-border"
                  : ""
              } `}
            >
              <span>
                {actionsBlocked} action{actionsBlocked === 1 ? " is" : "s are"}{" "}
                not possible
              </span>
              <ChevronDownIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="self-start">
            {open && (
              <div className=" w-72  p-4 ">
                <div className="text-crane-light-gray text-sm">
                  Not possible actions
                </div>
                <ul className="mt-2 space-y-2">
                  {availability.notPossible.map((key) => (
                    <ImpossibleAction
                      key={key}
                      actionKey={key}
                      open={openImpossibleAction === key}
                      onClick={() => handleImpossibleActionClick(key)}
                    />
                  ))}
                  {availability.notPossible.length === 0 && (
                    <li className="text-crane-gray text-sm">
                      All actions possible
                    </li>
                  )}
                </ul>

                <div className="mt-4 text-crane-light-gray text-sm">
                  Possible actions
                </div>
                <ul className="mt-2 space-y-2">
                  {availability.possible.map((key) => (
                    <li
                      key={key}
                      className="flex items-center justify-between py-2 border-b border-crane-darker last:border-b-0"
                    >
                      <span className="text-crane-white">
                        {craneActions[key].name}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </ClickAwayListener>
    </div>
  );
};
